import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    ButtonBase,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import type { FlowGroupId } from '../../flow/groups';
import { quoteWith } from '../../model/capabilities';
import { segmentedSx } from '../../chart/segmented';
import type { Outcome } from '../../model/types';
import type { OutcomeListProps } from '../OutcomeList';
import type { OutcomeSort } from '../ranking';
import { largestFlowByGroup } from './outcomeMeasure';
import { FOCUS_RING, PhoneOutcomeRow } from './PhoneOutcomeRow';

/** The market whose flow the row shows, as the desktop row reads it. */
const marketIdOf = (outcome: Outcome) =>
    quoteWith(outcome, 'flow')?.market.marketId;

const ACTION_SX = {
    minWidth: 0,
    minHeight: 44,
    padding: '0 12px',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: 'normal',
    letterSpacing: 'normal',
    textTransform: 'none',
} as const;

/**
 * The outcome list below 600 px: one card per outcome, the name first, the
 * figure the switch is on at its right, the bar under both. Same props and
 * the same handlers as the desktop table.
 *
 * Collapsed, it lists the outcomes the chart draws (`shown`, the same number
 * that has a coloured line) and, in their sorted places, any selected outcome
 * beyond them; "Show all N outcomes" lists the rest.
 */
export function PhoneOutcomeList({
    shown,
    hidden,
    selectedIds,
    colors,
    totalVolume,
    clickable,
    showFlow,
    latestFlow,
    sort,
    onSortChange,
    onToggle,
    onSelectAll,
    onClear,
}: OutcomeListProps) {
    const [expanded, setExpanded] = useState(false);
    const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
    const all = useMemo(() => [...shown, ...hidden], [shown, hidden]);
    const total = all.length;
    const collapsed = useMemo(
        () => all.filter((o, i) => i < shown.length || selected.has(o.id)),
        [all, shown.length, selected]
    );
    const canSelectAll =
        clickable &&
        onSelectAll !== undefined &&
        total > 1 &&
        selectedIds.length < total;
    const largest = useMemo(
        () =>
            sort === 'flow'
                ? largestFlowByGroup(
                      all.flatMap(o => marketIdOf(o) ?? []),
                      latestFlow
                  )
                : new Map<FlowGroupId, number>(),
        [all, latestFlow, sort]
    );
    const rows = expanded ? all : collapsed;

    // Where focus goes once the list has grown or shrunk: the toggle moves to
    // the end of a longer list, so focus would be left at the bottom.
    const toggleEl = useRef<HTMLButtonElement>(null);
    const rowEls = useRef(new Map<string, HTMLElement>());
    const focusAfter = useRef<string | 'toggle' | null>(null);
    useEffect(() => {
        const target = focusAfter.current;
        focusAfter.current = null;
        if (target === null) return;
        (target === 'toggle'
            ? toggleEl.current
            : rowEls.current.get(target)
        )?.focus();
    }, [expanded]);
    const toggleExpanded = () => {
        if (expanded) {
            focusAfter.current = 'toggle';
        } else {
            const listed = new Set(collapsed.map(o => o.id));
            focusAfter.current = all.find(o => !listed.has(o.id))?.id ?? null;
        }
        setExpanded(v => !v);
    };

    return (
        <Box
            data-testid="outcome-list"
            // The card around the list pads 12 px; the rows run edge to edge.
            // The page's body text tracks 0.00938em; the board has none.
            sx={{ m: -1.5, letterSpacing: 'normal' }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '6px 4px 0 16px',
                }}
            >
                <Box
                    sx={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}
                >
                    <Box
                        component="span"
                        sx={{
                            fontSize: '17px',
                            fontWeight: 600,
                            lineHeight: 'normal',
                        }}
                    >
                        Outcomes
                    </Box>
                    <Box
                        component="span"
                        sx={{
                            fontSize: '15px',
                            lineHeight: 'normal',
                            color: 'text.secondary',
                        }}
                    >
                        {total}
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {clickable && selectedIds.length > 0 && (
                        <Button
                            onClick={onClear}
                            aria-label="Clear selection"
                            sx={ACTION_SX}
                        >
                            Clear
                        </Button>
                    )}
                    {canSelectAll && (
                        <Button onClick={onSelectAll} sx={ACTION_SX}>
                            Show all on chart
                        </Button>
                    )}
                </Box>
            </Box>

            <Box sx={{ padding: '4px 16px 12px' }}>
                <ToggleButtonGroup
                    fullWidth
                    exclusive
                    aria-label="Measure"
                    value={sort}
                    onChange={(_, value: OutcomeSort | null) => {
                        if (value) onSortChange(value);
                    }}
                    sx={[
                        segmentedSx(8),
                        {
                            '& .MuiToggleButton-root': {
                                flex: '1 1 0',
                                minWidth: 0,
                                height: 44,
                                fontSize: '14px',
                                fontWeight: 400,
                                lineHeight: 'normal',
                                letterSpacing: 'normal',
                                textTransform: 'none',
                                color: (t: Theme) =>
                                    alpha(t.palette.text.primary, 0.7),
                                '&.Mui-selected, &.Mui-selected:hover': {
                                    fontWeight: 500,
                                    color: 'primary.main',
                                    bgcolor: (t: Theme) =>
                                        alpha(t.palette.primary.main, 0.12),
                                },
                            },
                        },
                    ]}
                >
                    <ToggleButton value="probability">Probability</ToggleButton>
                    <ToggleButton value="volume">Volume</ToggleButton>
                    {showFlow && <ToggleButton value="flow">Flow</ToggleButton>}
                </ToggleButtonGroup>
            </Box>

            {rows.map(outcome => {
                const marketId = marketIdOf(outcome);
                return (
                    <PhoneOutcomeRow
                        key={outcome.id}
                        outcome={outcome}
                        color={colors.get(outcome.id) ?? null}
                        selected={selected.has(outcome.id)}
                        clickable={clickable}
                        measure={sort}
                        totalVolume={totalVolume}
                        flow={marketId ? latestFlow.get(marketId) : undefined}
                        largestFlow={largest}
                        onToggle={() => onToggle(outcome.id)}
                        rowRef={el => {
                            if (el) rowEls.current.set(outcome.id, el);
                            else rowEls.current.delete(outcome.id);
                        }}
                    />
                );
            })}

            {collapsed.length < total && (
                <ButtonBase
                    ref={toggleEl}
                    onClick={toggleExpanded}
                    aria-expanded={expanded}
                    sx={{
                        '&.Mui-focusVisible': FOCUS_RING,
                        width: '100%',
                        // 48 px under the 1 px rule, as the board draws it
                        minHeight: 49,
                        borderTop: '1px solid',
                        borderTopColor: (t: Theme) =>
                            alpha(t.palette.text.primary, 0.08),
                        color: 'primary.main',
                        fontFamily: 'inherit',
                        fontSize: '14px',
                        fontWeight: 500,
                        lineHeight: 'normal',
                    }}
                >
                    {expanded ? 'Show fewer' : `Show all ${total} outcomes`}
                </ButtonBase>
            )}
        </Box>
    );
}
